import { useState } from 'react';
import { ListItem } from '../types';

interface ItemEditModalProps {
  item: ListItem;
  listType: 'SHOPPING' | 'TODO';
  onSave: (name: string, quantity: string, dueDate?: string, reminderMinutes?: number) => void;
  onDelete: () => void;
  onClose: () => void;
}

export function ItemEditModal({ item, listType, onSave, onDelete, onClose }: ItemEditModalProps) {
  const [name, setName] = useState(item.name);
  const [quantity, setQuantity] = useState(
    typeof item.quantity === 'number' ? item.quantity.toString() : (item.quantity || '')
  );
  
  // Format dueDate for datetime-local input (YYYY-MM-DDTHH:MM)
  const [dueDate, setDueDate] = useState(() => {
    if (item.dueDate) {
      const date = new Date(item.dueDate);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    }
    return '';
  });
  const [reminderMinutes, setReminderMinutes] = useState<number>(item.reminderMinutes ?? 0);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (name.trim()) {
      onSave(name.trim(), quantity.trim(), dueDate || undefined, reminderMinutes);
    }
  };

  const handleDelete = () => {
    onDelete();
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '1rem'
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: 'var(--bg-primary)',
          borderRadius: '12px',
          padding: '1.5rem',
          maxWidth: '500px',
          width: '100%',
          boxShadow: 'var(--shadow-xl)',
          color: 'var(--text-primary)',
          border: '1px solid var(--border-color)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 style={{ margin: '0 0 1.5rem 0', fontSize: '1.25rem', fontWeight: '600' }}>
          {listType === 'TODO' ? 'Aufgabe bearbeiten' : 'Artikel bearbeiten'}
        </h3>

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '500', color: 'var(--text-primary)' }}>
              {listType === 'TODO' ? 'Aufgabe' : 'Artikel'}
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
              className="input"
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '2px solid var(--border-color)',
                borderRadius: '8px',
                fontSize: '1rem',
                outline: 'none',
                transition: 'border-color 0.2s',
                backgroundColor: 'var(--bg-primary)',
                color: 'var(--text-primary)'
              }}
              onFocus={(e) => e.currentTarget.style.borderColor = 'var(--primary)'}
              onBlur={(e) => e.currentTarget.style.borderColor = 'var(--border-color)'}
            />
          </div>

          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '500', color: 'var(--text-primary)' }}>
              {listType === 'TODO' ? 'Notiz (optional)' : 'Menge/Info (optional)'}
            </label>
            <input
              type="text"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              className="input"
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '2px solid var(--border-color)',
                borderRadius: '8px',
                fontSize: '1rem',
                outline: 'none',
                transition: 'border-color 0.2s',
                backgroundColor: 'var(--bg-primary)',
                color: 'var(--text-primary)'
              }}
              onFocus={(e) => e.currentTarget.style.borderColor = 'var(--primary)'}
              onBlur={(e) => e.currentTarget.style.borderColor = 'var(--border-color)'}
            />
          </div>

          {listType === 'TODO' && (
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '500', color: 'var(--text-primary)' }}>
                ⏰ Fälligkeitsdatum (optional)
              </label>
              <input
                type="datetime-local"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                className="input"
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '2px solid var(--border-color)',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  outline: 'none',
                  transition: 'border-color 0.2s',
                  backgroundColor: 'var(--bg-primary)',
                  color: 'var(--text-primary)'
                }}
                onFocus={(e) => e.currentTarget.style.borderColor = 'var(--primary)'}
                onBlur={(e) => e.currentTarget.style.borderColor = 'var(--border-color)'}
              />
              {dueDate && (
                <>
                  <div style={{ marginTop: '0.75rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '500', color: 'var(--text-primary)' }}>
                      🔔 Erinnerung
                    </label>
                    <select
                      value={reminderMinutes}
                      onChange={(e) => setReminderMinutes(Number(e.target.value))}
                      className="input"
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '2px solid var(--border-color)',
                        borderRadius: '8px',
                        fontSize: '1rem',
                        backgroundColor: 'var(--bg-primary)',
                        color: 'var(--text-primary)',
                      }}
                    >
                      <option value={0}>Bei Fälligkeit</option>
                      <option value={15}>15 Minuten vorher</option>
                      <option value={30}>30 Minuten vorher</option>
                      <option value={60}>1 Stunde vorher</option>
                      <option value={120}>2 Stunden vorher</option>
                      <option value={1440}>1 Tag vorher</option>
                    </select>
                  </div>
                  <button
                    type="button"
                    onClick={() => { setDueDate(''); setReminderMinutes(0); }}
                    style={{
                      marginTop: '0.5rem',
                      fontSize: '0.875rem',
                      color: 'var(--text-tertiary)',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      textDecoration: 'underline'
                    }}
                  >
                    Datum entfernen
                  </button>
                </>
              )}
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
            <button
              type="button"
              onClick={handleDelete}
              style={{
                padding: '0.75rem',
                border: '2px solid var(--danger)',
                borderRadius: '8px',
                backgroundColor: 'var(--bg-primary)',
                color: 'var(--danger)',
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: '500',
                transition: 'background-color 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(239, 68, 68, 0.1)'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'var(--bg-primary)'}
            >
              🗑️ Löschen
            </button>
            <button
              type="submit"
              disabled={!name.trim()}
              style={{
                padding: '0.75rem',
                border: 'none',
                borderRadius: '8px',
                backgroundColor: name.trim() ? 'var(--primary)' : 'var(--bg-tertiary)',
                color: name.trim() ? '#ffffff' : 'var(--text-tertiary)',
                cursor: name.trim() ? 'pointer' : 'not-allowed',
                fontSize: '1rem',
                fontWeight: '500',
                transition: 'background-color 0.2s'
              }}
              onMouseEnter={(e) => {
                if (name.trim()) {
                  e.currentTarget.style.backgroundColor = 'var(--primary-dark)';
                }
              }}
              onMouseLeave={(e) => {
                if (name.trim()) {
                  e.currentTarget.style.backgroundColor = 'var(--primary)';
                }
              }}
            >
              💾 Speichern
            </button>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="btn btn-secondary"
            style={{
              marginTop: '0.75rem',
              width: '100%',
              padding: '0.75rem',
            }}
          >
            Abbrechen
          </button>
        </form>
      </div>
    </div>
  );
}
